import React, { useState, useEffect } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { HelperProfile } from '../types';
import { apiService } from '../services/api';
import { Loading } from '../components';
import HelperCard from '../components/HelperCard';
import { FileText, Bookmark, ClipboardList } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

const MyCandidatesPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [candidates, setCandidates] = useState<HelperProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('bookmarked');

  useEffect(() => {
    if (authLoading || !user?.id || user.role !== 'EMPLOYER') return;
    loadCandidates();
  }, [user, authLoading]);

  const loadCandidates = async () => {
    if (!user?.id) return;
    try {
      setLoading(true);
      const response = await apiService.candidateBookmarks.getMyBookmarks();
      if (response.success && response.data) {
        setCandidates(response.data);
      }
    } catch (error) {
      console.error('Error loading candidates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveBookmark = async (helper: HelperProfile) => {
    if (!helper.id) return;
    try {
      await apiService.candidateBookmarks.remove(helper.id);
      setCandidates(prev => prev.filter(c => c.id !== helper.id));
    } catch (error) {
      console.error('Error removing bookmark:', error);
    }
  };

  if (!user || user.role !== 'EMPLOYER') {
    return <Navigate to="/home" replace />;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-64px)]">
        <Loading fullScreen message={t('candidates.loading')} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-xl">{t('candidates.myTitle')}</CardTitle>
          <p className="text-sm text-muted-foreground">{t('candidates.myDesc')}</p>
        </CardHeader>
        <CardContent>
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="mb-8 p-1 h-auto bg-muted/50 w-full sm:w-auto grid grid-cols-2 sm:inline-flex">
          <TabsTrigger value="bookmarked" className="flex items-center gap-2 py-3 sm:px-6 data-[state=active]:bg-background data-[state=active]:shadow-sm">
            <Bookmark className="h-4 w-4" />
            <span className="font-medium">{t('common.bookmarked')} ({candidates.length})</span>
          </TabsTrigger>
          <TabsTrigger value="applied" className="flex items-center gap-2 py-3 sm:px-6 data-[state=active]:bg-background data-[state=active]:shadow-sm">
            <ClipboardList className="h-4 w-4" />
            <span className="font-medium">{t('candidates.applied')}</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="bookmarked" className="mt-0 focus-visible:outline-none focus-visible:ring-0">
          {candidates.length === 0 ? (
            <Card className="border-dashed bg-muted/10">
              <CardContent className="flex flex-col items-center justify-center py-20 px-4 text-center">
                <div className="h-20 w-20 rounded-full bg-muted flex items-center justify-center mb-6">
                  <FileText className="h-10 w-10 text-muted-foreground" />
                </div>
                <h2 className="text-2xl font-semibold mb-3 text-foreground">{t('candidates.noBookmarks')}</h2>
                <p className="text-muted-foreground max-w-md mb-8 text-lg">
                  {t('candidates.noBookmarksDesc')}
                </p>
                <Button size="lg" onClick={() => navigate('/candidates')}>
                  {t('candidates.goToSearch')}
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {candidates.map((helper) => (
                <HelperCard
                  key={helper.id}
                  helper={helper}
                  isBookmarked={true}
                  onViewDetail={() => navigate(`/helper/${helper.id}`)}
                  onBookmark={() => handleRemoveBookmark(helper)}
                />
              ))}
            </div>
          )}
        </TabsContent>
        
        <TabsContent value="applied" className="mt-0 focus-visible:outline-none focus-visible:ring-0">
          <Card className="border-dashed bg-muted/10">
            <CardContent className="flex flex-col items-center justify-center py-20 px-4 text-center">
              <div className="h-20 w-20 rounded-full bg-muted flex items-center justify-center mb-6">
                <ClipboardList className="h-10 w-10 text-muted-foreground" />
              </div>
              <h2 className="text-2xl font-semibold mb-3 text-foreground">{t('candidates.noApplied')}</h2>
              <p className="text-muted-foreground max-w-md text-lg">
                {t('candidates.noAppliedDesc')}
              </p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default MyCandidatesPage;
